import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorLogRow } from "@/components/admin/ErrorLogRow";
import { StorageCleanup } from "@/components/admin/StorageCleanup";

interface ErrorLog {
  id: string;
  template_id: string;
  processing_stage: string;
  error_message: string;
  created_at: string;
  retry_count: number;
  templates?: {
    filename: string;
  };
}

interface ProcessingStat {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export default function AdminDashboard() {
  const [pendingReviews, setPendingReviews] = useState(0);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [processingStats, setProcessingStats] = useState<ProcessingStat>({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });
  const [recentErrors, setRecentErrors] = useState<ErrorLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get pending reviews count
        const { count: reviewCount } = await supabase
          .from('templates')
          .select('id', { count: 'exact', head: true })
          .eq('needs_review', true);

        // Get total templates count
        const { count: templateCount } = await supabase
          .from('templates')
          .select('id', { count: 'exact', head: true });

        // Get total documents count
        const { count: documentCount } = await supabase
          .from('documents')
          .select('id', { count: 'exact', head: true });

        // Get processing statistics
        const { data: statusCounts } = await supabase
          .from('templates')
          .select('processing_status')
          .not('processing_status', 'is', null);

        const stats: ProcessingStat = {
          pending: 0,
          processing: 0,
          completed: 0,
          failed: 0
        };

        statusCounts?.forEach(row => {
          const status = row.processing_status as keyof ProcessingStat;
          if (status in stats) {
            stats[status]++;
          }
        });

        // Get recent processing errors
        const { data: errors } = await supabase
          .from('error_logs')
          .select(`
            id,
            template_id,
            processing_stage,
            error_message,
            created_at,
            retry_count,
            templates (filename)
          `)
          .not('template_id', 'is', null)
          .order('created_at', { ascending: false })
          .limit(10);

        setPendingReviews(reviewCount || 0);
        setTotalTemplates(templateCount || 0);
        setTotalDocuments(documentCount || 0);
        setProcessingStats(stats);
        setRecentErrors(errors as ErrorLog[] || []);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const retryProcessing = async (templateId: string) => {
    try {
      await supabase.functions.invoke('process-template-async', {
        body: { templateId }
      });
      
      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error retrying processing:', error);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse">Loading dashboard data...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Admin Dashboard</h1>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Pending Reviews</CardTitle>
            <CardDescription>Templates awaiting review</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{pendingReviews}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Total Templates</CardTitle>
            <CardDescription>Available in the system</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{totalTemplates}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Total Documents</CardTitle>
            <CardDescription>Created by users</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{totalDocuments}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Processing Queue</CardTitle>
            <CardDescription>Current processing status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm">Pending:</span>
              <Badge variant="outline">{processingStats.pending}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Processing:</span>
              <Badge variant="outline">{processingStats.processing}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Completed:</span>
              <Badge variant="outline">{processingStats.completed}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Failed:</span>
              <Badge variant="destructive">{processingStats.failed}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {processingStats.failed > 0 && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Processing Failures Detected</AlertTitle>
          <AlertDescription>
            {processingStats.failed} template{processingStats.failed > 1 ? 's' : ''} failed to process. Check the error logs below for details.
          </AlertDescription>
        </Alert>
      )}

      {/* Add Storage Cleanup Component */}
      <StorageCleanup />

      {recentErrors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Processing Errors</CardTitle>
            <CardDescription>Latest template processing failures</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentErrors.map((error) => (
                  <ErrorLogRow
                    key={error.id}
                    error={error}
                    onRetry={retryProcessing}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
